import { loadXlsx } from '../../shared/utils/loadXlsx'
import type {
  SupplierImportRow,
  SupplierPaymentType,
  SupplierType,
} from '../../shared/api/suppliers.api'

// Парсер Excel-файла справочника поставщиков (формат файла РОЗ «Условия/
// Объём CNY»). Колонки ищутся по заголовкам первой строки (регистронезависимо,
// по подстроке) — порядок и лишние колонки не важны:
//   «Поставщик» (обязательная), «Тип», «Ответственный», «Условия оплаты»,
//   «Лимит», «Валюта», «NET», «Гарант», «Реклам» (кол-во), «Примечание».

const TYPE_BY_TEXT: Record<string, SupplierType> = {
  производитель: 'MANUFACTURER',
  дистрибьютор: 'DISTRIBUTOR',
  дистрибутор: 'DISTRIBUTOR',
  агент: 'AGENT',
  трейдер: 'AGENT',
  площадка: 'PLATFORM',
}

// Классификация текста условий оплаты → enum (по разбору 24 текстов из файла).
export function classifyPaymentType(raw: string): SupplierPaymentType | null {
  const s = raw.toLowerCase()
  if (!s.trim()) return null
  if (/(100\s*%|полн)/.test(s) && /(prepay|предопл|t\/t)/.test(s)) return 'PREPAY_100'
  if (/50\s*[/\\-]\s*50/.test(s)) return 'PREPAY_50_50'
  if (/30\s*[/\\-]\s*70/.test(s)) return 'PREPAY_30_70'
  if (/(afterpay|постоплат|кредит)/.test(s)) return 'AFTERPAY_CREDIT_LINE'
  if (/размещен/.test(s)) return 'PLACEMENT_LIMIT'
  if (/(до\s*[¥￥$]?\d|порог)/.test(s)) return 'MIXED_THRESHOLD'
  if (/(договор|обсужда|уточн)/.test(s)) return 'NEGOTIABLE'
  return null
}

function toInt(v: unknown): number | null {
  const n = parseInt(String(v ?? '').replace(/[^\d-]/g, ''), 10)
  return isNaN(n) ? null : n
}
function toNum(v: unknown): number | null {
  const n = parseFloat(
    String(v ?? '')
      .replace(/\s/g, '')
      .replace(',', '.')
      .replace(/[^\d.-]/g, ''),
  )
  return isNaN(n) ? null : n
}

export async function parseSuppliersExcel(file: File): Promise<SupplierImportRow[]> {
  const buf = await file.arrayBuffer()
  const XLSX = await loadXlsx()
  const wb = XLSX.read(buf, { type: 'array' })
  const sheetName = wb.SheetNames[0]
  if (!sheetName) return []
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[sheetName]!, {
    defval: '',
  })
  if (rows.length === 0) return []

  const keys = Object.keys(rows[0]!)
  const findKey = (...needles: string[]) =>
    keys.find((k) => needles.some((n) => k.toLowerCase().includes(n)))
  const nameKey = findKey('поставщик', 'supplier', 'название')
  if (!nameKey) throw new Error('Не найдена колонка «Поставщик» в первой строке файла')
  const typeKey = findKey('тип')
  const buyerKey = findKey('ответствен', 'закупщик')
  const paymentKey = findKey('условия оплат', 'оплат')
  const limitKey = findKey('лимит')
  const currencyKey = findKey('валют')
  const netKey = findKey('net', 'дней после')
  const warrantyKey = findKey('гарант')
  const complaintsKey = findKey('реклам')
  const notesKey = findKey('примечан', 'заметк')

  // Нет колонки, пустая или нераспознанная ячейка → поле в строку НЕ кладём
  // (undefined не уходит в JSON). Сервер не трогает такие поля у существующего
  // поставщика — повторный импорт не стирает ручные данные карточки (№85).
  const out: SupplierImportRow[] = []
  for (const r of rows) {
    const cell = (key: string | undefined) => (key ? String(r[key] ?? '').trim() : '')
    const name = cell(nameKey)
    if (!name) continue
    const typeText = cell(typeKey).toLowerCase()
    const type = typeText
      ? Object.entries(TYPE_BY_TEXT).find(([needle]) => typeText.includes(needle))?.[1]
      : undefined
    const paymentText = cell(paymentKey)
    const complaintsRaw = cell(complaintsKey)
    const complaintsNum = toInt(complaintsRaw)
    out.push({
      name,
      type,
      responsibleBuyerName: cell(buyerKey) || undefined,
      paymentType: classifyPaymentType(paymentText) ?? undefined,
      creditLimitAmount: toNum(cell(limitKey)) ?? undefined,
      creditLimitCurrency: cell(currencyKey).toUpperCase().slice(0, 3) || undefined,
      postShipmentDays: toInt(cell(netKey)) ?? undefined,
      warrantyDays: toInt(cell(warrantyKey)) ?? undefined,
      complaintsCount: complaintsNum ?? undefined,
      // Текст рекламаций («3 шт, ждём замену») сохраняем как заметку целиком.
      complaintsNotes: complaintsRaw && complaintsNum === null ? complaintsRaw : undefined,
      // Полный текст условий — в спецусловия (enum теряет детали вроде
      // «разъёмы 50/50»), плюс примечание из отдельной колонки.
      paymentNotes: [paymentText, cell(notesKey)].filter(Boolean).join('\n') || undefined,
    })
  }
  return out
}
